import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Button from '../../../reusable/Button/Button';
import Input from '../../../reusable/Input/Input';
import { toast } from '../../../reusable/Toast/toastStore';
import { useParticipateInCompetitionMutation } from '../../../store/api/competitionsApi';
import { useGetProfileDetailsQuery } from '../../../store/api/profileApi';
import { selectUser } from '../../../store/slices/authSlice';
import { t } from '../../../i18n/i18n';
import './CompetitionDetail.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const ENTRY_FAILED = 'Please try again.';

/**
 * A refusal is either `{ detail: "…" }` or per-field `{ basicDetails: ["…"] }`,
 * so the banner can say what was actually wrong rather than that something was.
 */
const entryErrorMessage = (error) => {
  const body = error?.data;
  if (typeof body === 'string') return body;

  const first = body?.detail ?? Object.values(body ?? {})[0];
  const message = Array.isArray(first) ? first[0] : first;
  return typeof message === 'string' && message ? message : ENTRY_FAILED;
};

/**
 * Only the two fields the trader types. `name` is not here because it is not
 * theirs to get wrong — it comes off their profile, and a validator over a
 * field nobody can edit would only ever fire on a profile problem, which the
 * card says in its own words below.
 */
const entrySchema = Yup.object({
  basicDetails: Yup.string()
    .trim()
    .required('Choose a nickname')
    .min(3, 'At least 3 characters')
    .max(30, 'At most 30 characters'),
  pwd: Yup.string().required('Enter a password'),
});

const EMPTY = { basicDetails: '', pwd: '' };

/**
 * Entering a competition — POST /v2/competition/participate/.
 *
 * Rendered between the hero and the leaderboard on Competition Detail, and
 * only while there is something to enter: the parent drops it once the
 * competition has ended, and it draws its entered state instead of the form
 * once the trader is in.
 */
export default function ParticipateCard({ competitionId, joined }) {
  const [participate, { isLoading: isSubmitting }] = useParticipateInCompetitionMutation();

  // The name the entry is filed under. The profile record is the authority on
  // it; the session's cached name is what the shell already shows in the
  // sidebar, and covers the moment before the profile call lands.
  const user = useSelector(selectUser);
  const { data: profile, isLoading: profileLoading } = useGetProfileDetailsQuery();

  const name = (
    profile?.full_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
    user?.name ||
    ''
  ).trim();

  // A profile with no name on it can't be entered under one. Better to say so
  // than to POST an empty string and let the backend phrase it.
  const nameMissing = !profileLoading && !name;

  const formik = useFormik({
    initialValues: EMPTY,
    validationSchema: entrySchema,
    onSubmit: async (values, { resetForm, setStatus }) => {
      setStatus(null);

      try {
        await participate({
          // The route hands it over as a string, and the payload's is a number.
          competition_id: Number(competitionId),
          name,
          pwd: values.pwd,
          basicDetails: values.basicDetails.trim(),
        }).unwrap();

        // The card flips to its entered state on its own: the mutation
        // invalidates `Competition`, the detail call refetches, and `joined`
        // comes back true. Clearing the fields is so the password does not sit
        // in the form in the meantime.
        resetForm();
        toast.success(
          t('You are entered'),
          t('Good luck — the leaderboard updates as trades close.')
        );
      } catch (err) {
        const message = entryErrorMessage(err);
        setStatus({ error: message });
        toast.error(t('Could not enter competition'), t(message));
      }
    },
  });

  /** Only show a field error once the user has left the field or tried to submit. */
  const fieldError = (field) => (formik.touched[field] ? t(formik.errors[field]) : undefined);

  const field = (key) => ({
    name: key,
    value: formik.values[key],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: fieldError(key),
    disabled: isSubmitting,
  });

  if (joined) {
    return (
      <section className="pt-card pt-card--entered">
        <header className="pt-card__head">
          <span className="pt-card__title">
            <span className="icon pt-card__icon" style={icon('nav-competition')} />
            {t('You are in this competition')}
          </span>
          <span className="pt-card__meta pt-card__meta--ok">
            <span className="pt-card__mark">
              <span className="icon pt-card__tick" style={icon('check-sm')} />
            </span>
            {t('Entry confirmed')}
          </span>
        </header>
        <p className="pt-card__desc">
          {t('Your standing is on the leaderboard below, and updates as your trades close.')}
        </p>
      </section>
    );
  }

  return (
    <section className="pt-card" id="participate">
      <header className="pt-card__head">
        <span className="pt-card__title">
          <span className="icon pt-card__icon" style={icon('nav-competition')} />
          {t('Enter this competition')}
        </span>
        <span className="pt-card__meta">{t('Free to enter')}</span>
      </header>

      <p className="pt-card__desc">
        {t(
          'You are entered under the name on your profile. The nickname is what the leaderboard shows you by.'
        )}
      </p>

      {/* Whatever the API refused the whole entry for. Field-level problems
          never reach here — they sit under the field they belong to. */}
      {formik.status?.error && (
        <p className="pt-banner" role="alert">
          {t(formik.status.error)}
        </p>
      )}

      {nameMissing && (
        <p className="pt-banner" role="alert">
          {t('Add your first and last name under')}{' '}
          <Link to="/profile" className="pt-banner__link">
            {t('My Profile')}
          </Link>{' '}
          {t('before entering.')}
        </p>
      )}

      <form className="pt-card__grid" onSubmit={formik.handleSubmit} noValidate>
        <Input
          label={t('Name')}
          value={profileLoading ? '' : name}
          placeholder={profileLoading ? t('Loading…') : t('Set your name on your profile')}
          hint={t('From your profile')}
          disabled
          readOnly
        />
        <Input
          label={t('Nickname')}
          placeholder={t('How the leaderboard shows you')}
          autoComplete="nickname"
          required
          {...field('basicDetails')}
        />
        <Input
          label={t('Password')}
          type="password"
          placeholder={t('Enter a password')}
          autoComplete="new-password"
          required
          {...field('pwd')}
        />
      </form>

      <div className="pt-card__actions">
        <Button
          variant="submit"
          loading={isSubmitting}
          disabled={nameMissing || profileLoading}
          onClick={formik.handleSubmit}
        >
          {t('Enter competition')}
        </Button>
      </div>
    </section>
  );
}
